/**
 * The window for adding an employee and for editing one.
 *
 * One form for both, so a field means the same thing in either. As the pay is
 * typed, the month and the hour it makes are worked out underneath with the
 * rules in force — the same arithmetic as the cost screen — so whoever enters
 * a salary sees at once what the airport will pay for it, GOSI and all.
 *
 * Editing sends only what changed; the platform audits each change.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useHrAddPerson, useHrEditPerson, useHrPeople, useHrRules } from "../../api/queries";
import { Button, Field, Input, Select } from "../../design/components";
import {
  costLine,
  describeEmployee,
  mergeRules,
  NATIONALITIES,
  STATUS_LABEL,
  TRADE_LABEL,
  TRADES,
  type ContractType,
  type EmployeeStatus,
  type Employment,
  type HrEmployee,
  type HrEmployeeInput,
  type HrEmployeePatch,
  type Trade,
} from "../../domain/hr";
import { t } from "../../i18n";
import { useHrAccess } from "./access";
import { useShowHourly } from "./kit";
import { Err, rate, s, sar, Section } from "./shared";

interface Form {
  employeeNo: string;
  name: string;
  nationality: string;
  department: string;
  trade: string;
  jobTitle: string;
  grade: string;
  employment: Employment;
  contractType: ContractType;
  hiredOn: string;
  contractEnd: string;
  status: EmployeeStatus;
  leftOn: string;
  weeklyHours: string;
  basic: string;
  housing: string;
  transport: string;
  other: string;
  outsourcedMonthly: string;
  medicalAnnual: string;
  gosiNewEntrant: "" | "yes" | "no";
  principalId: string;
  costCentre: string;
  notes: string;
  dateOfBirth: string;
  gender: "" | "male" | "female";
  maritalStatus: "" | "single" | "married" | "divorced" | "widowed";
  idNumber: string;
  idExpiry: string;
  passportNo: string;
  passportExpiry: string;
  phone: string;
  email: string;
  address: string;
  ecName: string;
  ecRelation: string;
  ecPhone: string;
}

const EMPTY: Form = {
  employeeNo: "",
  name: "",
  nationality: "SA",
  department: "",
  trade: "",
  jobTitle: "",
  grade: "",
  employment: "direct",
  contractType: "indefinite",
  hiredOn: "",
  contractEnd: "",
  status: "active",
  leftOn: "",
  weeklyHours: "48",
  basic: "",
  housing: "",
  transport: "",
  other: "",
  outsourcedMonthly: "",
  medicalAnnual: "",
  gosiNewEntrant: "",
  principalId: "",
  costCentre: "",
  notes: "",
  dateOfBirth: "",
  gender: "",
  maritalStatus: "",
  idNumber: "",
  idExpiry: "",
  passportNo: "",
  passportExpiry: "",
  phone: "",
  email: "",
  address: "",
  ecName: "",
  ecRelation: "",
  ecPhone: "",
};

const str = (v: number | null | undefined) => (v == null ? "" : String(v));

function formOf(e: HrEmployee): Form {
  return {
    employeeNo: e.employeeNo,
    name: e.name,
    nationality: e.nationality,
    department: e.department ?? "",
    trade: e.trade ?? "",
    jobTitle: e.jobTitle ?? "",
    grade: e.grade ?? "",
    employment: e.employment,
    contractType: e.contractType,
    hiredOn: e.hiredOn,
    contractEnd: e.contractEnd ?? "",
    status: e.status,
    leftOn: e.leftOn ?? "",
    weeklyHours: String(e.weeklyHours),
    basic: e.basic ? String(e.basic) : "",
    housing: e.housing ? String(e.housing) : "",
    transport: e.transport ? String(e.transport) : "",
    other: e.other ? String(e.other) : "",
    outsourcedMonthly: str(e.outsourcedMonthly),
    medicalAnnual: str(e.medicalAnnual),
    gosiNewEntrant: e.gosiNewEntrant == null ? "" : e.gosiNewEntrant ? "yes" : "no",
    principalId: e.principalId ?? "",
    costCentre: e.costCentre ?? "",
    notes: e.notes ?? "",
    dateOfBirth: e.dateOfBirth ?? "",
    gender: e.gender ?? "",
    maritalStatus: e.maritalStatus ?? "",
    idNumber: e.idNumber ?? "",
    idExpiry: e.idExpiry ?? "",
    passportNo: e.passportNo ?? "",
    passportExpiry: e.passportExpiry ?? "",
    phone: e.phone ?? "",
    email: e.email ?? "",
    address: e.address ?? "",
    ecName: e.emergencyContact?.name ?? "",
    ecRelation: e.emergencyContact?.relation ?? "",
    ecPhone: e.emergencyContact?.phone ?? "",
  };
}

const num = (v: string) => (v.trim() === "" ? 0 : Number(v));
const numN = (v: string) => (v.trim() === "" ? null : Number(v));
const txt = (v: string) => (v.trim() === "" ? null : v.trim());

function inputOf(f: Form): HrEmployeeInput {
  const outsourced = f.employment === "outsourced";
  return {
    siteId: null,
    employeeNo: f.employeeNo.trim(),
    name: f.name.trim(),
    nationality: f.nationality.trim().toUpperCase(),
    department: txt(f.department),
    trade: (txt(f.trade) as Trade | null) ?? null,
    jobTitle: txt(f.jobTitle),
    grade: txt(f.grade),
    employment: f.employment,
    contractType: f.contractType,
    hiredOn: f.hiredOn,
    contractEnd: txt(f.contractEnd),
    leftOn: f.status === "left" ? txt(f.leftOn) : null,
    status: f.status,
    basic: outsourced ? 0 : num(f.basic),
    housing: outsourced ? 0 : num(f.housing),
    transport: outsourced ? 0 : num(f.transport),
    other: outsourced ? 0 : num(f.other),
    outsourcedMonthly: outsourced ? numN(f.outsourcedMonthly) : null,
    medicalAnnual: outsourced ? null : numN(f.medicalAnnual),
    gosiNewEntrant: f.gosiNewEntrant === "" ? null : f.gosiNewEntrant === "yes",
    weeklyHours: num(f.weeklyHours) || 48,
    costCentre: txt(f.costCentre),
    principalId: txt(f.principalId),
    notes: txt(f.notes),
    dateOfBirth: txt(f.dateOfBirth),
    gender: f.gender || null,
    maritalStatus: f.maritalStatus || null,
    idNumber: txt(f.idNumber),
    idExpiry: txt(f.idExpiry),
    passportNo: txt(f.passportNo),
    passportExpiry: txt(f.passportExpiry),
    phone: txt(f.phone),
    email: txt(f.email),
    address: txt(f.address),
    emergencyContact:
      f.ecName.trim() || f.ecPhone.trim()
        ? { name: f.ecName.trim(), relation: f.ecRelation.trim(), phone: f.ecPhone.trim() }
        : null,
  };
}

const PAY_KEYS = [
  "basic",
  "housing",
  "transport",
  "other",
  "outsourcedMonthly",
  "medicalAnnual",
  "gosiNewEntrant",
] as const;

export function EmployeeDialog({ employee, onClose }: { employee?: HrEmployee; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open && typeof d.showModal === "function") d.showModal();
  }, []);
  return (
    <dialog ref={ref} onClose={onClose} className={s.dialog} data-testid="hr-employee-dialog">
      <EmployeeForm employee={employee} onClose={onClose} />
    </dialog>
  );
}

function EmployeeForm({ employee, onClose }: { employee?: HrEmployee; onClose: () => void }) {
  const adding = !employee;
  const people = useHrPeople();
  const rulesQ = useHrRules();
  const add = useHrAddPerson();
  const edit = useHrEditPerson(employee?.id ?? "");
  const [f, setF] = useState<Form>(() => (employee ? formOf(employee) : EMPTY));
  const [tried, setTried] = useState(false);
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const outsourced = f.employment === "outsourced";
  const canPay = useHrAccess().can.pay && !employee?.payHidden;
  const showHourly = useShowHourly();

  const problems: Record<string, string> = {};
  if (!f.employeeNo.trim()) problems.employeeNo = t("An employee number is needed.");
  else if (people.data?.employees.some((e) => e.employeeNo === f.employeeNo.trim() && e.id !== employee?.id))
    problems.employeeNo = t("This employee number is already used.");
  if (f.name.trim().length < 2) problems.name = t("A name is needed.");
  if (!/^[A-Za-z]{2}$/.test(f.nationality.trim())) problems.nationality = t("A two-letter country code, e.g. SA.");
  if (!f.hiredOn) problems.hiredOn = t("Hire date is required. Service, leave, and end of service count from it.");
  if (f.status === "left" && !f.leftOn) problems.leftOn = t("A leaver needs the date they left.");
  if (f.status === "left" && f.leftOn && f.hiredOn && f.leftOn < f.hiredOn)
    problems.leftOn = t("They cannot leave before they were hired.");
  for (const k of ["basic", "housing", "transport", "other", "outsourcedMonthly", "medicalAnnual"] as const)
    if (f[k].trim() && !(Number(f[k]) >= 0)) problems[k] = t("Must be a number.");
  const wh = Number(f.weeklyHours);
  if (!(wh >= 1 && wh <= 72)) problems.weeklyHours = t("Between 1 and 72 hours.");
  if (f.principalId) {
    const clash = people.data?.employees.find((e) => e.principalId === f.principalId && e.id !== employee?.id);
    if (clash) problems.principalId = t("That user is already bound to {no}.", { no: clash.employeeNo });
  }
  const show = (k: string) => (tried ? problems[k] : undefined);

  /* the month and the hour this form makes, with the rules in force */
  const preview = useMemo(() => {
    if (!f.hiredOn) return null;
    const rules = rulesQ.data?.rules ?? mergeRules(null);
    const input = inputOf(f);
    const e = describeEmployee({ ...input, id: employee?.id ?? "new", principalName: null }, rules, new Date());
    return costLine(e, rules, new Date());
  }, [f, rulesQ.data, employee?.id]);

  const pending = add.isPending || edit.isPending;
  const failed = add.error ?? edit.error;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setTried(true);
    if (Object.keys(problems).length) return;
    const input = inputOf(f);
    if (!canPay) for (const k of PAY_KEYS) delete (input as Partial<HrEmployeeInput>)[k];
    if (adding) {
      add.mutate(input, { onSuccess: onClose });
      return;
    }
    const before = inputOf(formOf(employee as HrEmployee));
    const patch: HrEmployeePatch = {};
    for (const k of Object.keys(input) as (keyof HrEmployeeInput)[])
      if (k !== "siteId" && JSON.stringify(input[k] ?? null) !== JSON.stringify(before[k] ?? null))
        (patch as Record<string, unknown>)[k] = input[k];
    if (!Object.keys(patch).length) {
      onClose();
      return;
    }
    edit.mutate(patch, { onSuccess: onClose });
  };

  const departments = people.data?.departments ?? [];
  const users = people.data?.users ?? [];

  return (
    <form onSubmit={submit} className={s.dialogForm} noValidate>
      <h2>{adding ? t("Add an employee") : t("Edit {name}", { name: employee?.name ?? "" })}</h2>

      <Section title={t("Who")}>
        <Field label="Employee number">
          <Input value={f.employeeNo} onChange={set("employeeNo")} data-testid="hr-f-no" autoFocus={adding} />
          <Err text={show("employeeNo")} />
        </Field>
        <Field label="Name">
          <Input value={f.name} onChange={set("name")} data-testid="hr-f-name" />
          <Err text={show("name")} />
        </Field>
        <Field label="Nationality">
          <Input value={f.nationality} onChange={set("nationality")} list="hr-nationalities" maxLength={2} />
          <datalist id="hr-nationalities">
            {NATIONALITIES.map((n) => (
              <option key={n.code} value={n.code}>
                {t(n.name)}
              </option>
            ))}
          </datalist>
          <Err text={show("nationality")} />
        </Field>
        <Field label="Department">
          <Input value={f.department} onChange={set("department")} list="hr-departments" />
          <datalist id="hr-departments">
            {departments.map((d) => (
              <option key={d} value={d} />
            ))}
          </datalist>
        </Field>
        <Field label="Trade">
          <Select value={f.trade} onChange={set("trade")} data-testid="hr-f-trade">
            <option value="">{t("None")}</option>
            {TRADES.map((tr) => (
              <option key={tr} value={tr}>
                {t(TRADE_LABEL[tr])}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Job title">
          <Input value={f.jobTitle} onChange={set("jobTitle")} />
        </Field>
        <Field label="Grade">
          <Input value={f.grade} onChange={set("grade")} placeholder={t("optional")} />
        </Field>
      </Section>

      <Section title={t("Personal details")}>
        <Field label="Date of birth">
          <Input type="date" value={f.dateOfBirth} onChange={set("dateOfBirth")} data-testid="hr-f-dob" />
        </Field>
        <Field label="Gender">
          <Select value={f.gender} onChange={set("gender")}>
            <option value="">—</option>
            <option value="male">{t("Male")}</option>
            <option value="female">{t("Female")}</option>
          </Select>
        </Field>
        <Field label="Marital status">
          <Select value={f.maritalStatus} onChange={set("maritalStatus")}>
            <option value="">—</option>
            {(["single", "married", "divorced", "widowed"] as const).map((m) => (
              <option key={m} value={m}>
                {t(m[0].toUpperCase() + m.slice(1))}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={f.nationality.trim().toUpperCase() === "SA" ? "National ID number" : "Iqama number"}>
          <Input value={f.idNumber} onChange={set("idNumber")} />
        </Field>
        <Field label="ID / iqama expires">
          <Input type="date" value={f.idExpiry} onChange={set("idExpiry")} />
        </Field>
        {f.nationality.trim().toUpperCase() !== "SA" && (
          <>
            <Field label="Passport number">
              <Input value={f.passportNo} onChange={set("passportNo")} />
            </Field>
            <Field label="Passport expires">
              <Input type="date" value={f.passportExpiry} onChange={set("passportExpiry")} />
            </Field>
          </>
        )}
        <Field label="Mobile">
          <Input value={f.phone} onChange={set("phone")} inputMode="tel" placeholder="+9665…" />
        </Field>
        <Field label="Email">
          <Input value={f.email} onChange={set("email")} inputMode="email" />
        </Field>
        <Field label="Address">
          <Input value={f.address} onChange={set("address")} />
        </Field>
        <Field label="Emergency contact">
          <Input value={f.ecName} onChange={set("ecName")} placeholder={t("Name")} />
        </Field>
        <Field label="Relation">
          <Input value={f.ecRelation} onChange={set("ecRelation")} placeholder={t("e.g. Brother")} />
        </Field>
        <Field label="Their mobile">
          <Input value={f.ecPhone} onChange={set("ecPhone")} inputMode="tel" />
        </Field>
      </Section>

      <Section title={t("Contract")}>
        <Field label="Employment">
          <Select
            value={f.employment}
            onChange={(e) => setF({ ...f, employment: e.target.value as Employment })}
            data-testid="hr-f-employment"
          >
            <option value="direct">{t("Direct employee")}</option>
            <option value="outsourced">{t("Outsourced (contractor's head)")}</option>
          </Select>
        </Field>
        <Field label="Contract">
          <Select value={f.contractType} onChange={(e) => setF({ ...f, contractType: e.target.value as ContractType })}>
            <option value="indefinite">{t("Indefinite")}</option>
            <option value="fixed">{t("Fixed term")}</option>
          </Select>
        </Field>
        <Field label="Hired on">
          <Input type="date" value={f.hiredOn} onChange={set("hiredOn")} data-testid="hr-f-hired" />
          <Err text={show("hiredOn")} />
        </Field>
        <Field label="Contract ends">
          <Input type="date" value={f.contractEnd} onChange={set("contractEnd")} />
        </Field>
        <Field label="Status">
          <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value as EmployeeStatus })}>
            {(Object.keys(STATUS_LABEL) as EmployeeStatus[]).map((st) => (
              <option key={st} value={st}>
                {t(STATUS_LABEL[st])}
              </option>
            ))}
          </Select>
        </Field>
        {f.status === "left" && (
          <Field label="Left on">
            <Input type="date" value={f.leftOn} onChange={set("leftOn")} />
            <Err text={show("leftOn")} />
          </Field>
        )}
        <Field label="Contracted hours a week">
          <Input value={f.weeklyHours} onChange={set("weeklyHours")} inputMode="decimal" />
          <Err text={show("weeklyHours")} />
        </Field>
      </Section>

      {!canPay ? (
        <p className={s.note}>
          {t("Pay is hidden without the pay permission. You can edit everything else.")}
        </p>
      ) : outsourced ? (
        <Section title={t("What the contractor charges")}>
          <Field label="Monthly charge for this head (SAR)">
            <Input
              value={f.outsourcedMonthly}
              onChange={set("outsourcedMonthly")}
              inputMode="decimal"
              data-testid="hr-f-contract"
            />
            <Err text={show("outsourcedMonthly")} />
          </Field>
          <p className={s.note} style={{ gridColumn: "1 / -1" }}>
            {t(
              "Costs only the contract charge; the contractor pays GOSI, end of service, levy, and fees. Not counted in the airport's Nitaqat.",
            )}
          </p>
        </Section>
      ) : (
        <Section title={t("Monthly pay (SAR)")}>
          <Field label="Basic">
            <Input value={f.basic} onChange={set("basic")} inputMode="decimal" data-testid="hr-f-basic" />
            <Err text={show("basic")} />
          </Field>
          <Field label="Housing">
            <Input value={f.housing} onChange={set("housing")} inputMode="decimal" data-testid="hr-f-housing" />
            <Err text={show("housing")} />
          </Field>
          <Field label="Transport">
            <Input value={f.transport} onChange={set("transport")} inputMode="decimal" />
            <Err text={show("transport")} />
          </Field>
          <Field label="Other fixed allowances">
            <Input value={f.other} onChange={set("other")} inputMode="decimal" />
            <Err text={show("other")} />
          </Field>
          <Field label="Medical premium a year (blank: the default)">
            <Input value={f.medicalAnnual} onChange={set("medicalAnnual")} inputMode="decimal" />
            <Err text={show("medicalAnnual")} />
          </Field>
          {f.nationality.trim().toUpperCase() === "SA" && (
            <Field label="First registered with GOSI on or after 3 July 2024">
              <Select
                value={f.gosiNewEntrant}
                onChange={(e) => setF({ ...f, gosiNewEntrant: e.target.value as Form["gosiNewEntrant"] })}
              >
                <option value="">{t("Not recorded — assume from the hire date")}</option>
                <option value="yes">{t("Yes — the 2024 schedule")}</option>
                <option value="no">{t("No — registered before")}</option>
              </Select>
            </Field>
          )}
        </Section>
      )}

      <Section title={t("Where the cost goes")}>
        <Field label="Platform user (who closes their work orders)">
          <Select value={f.principalId} onChange={set("principalId")} data-testid="hr-f-user">
            <option value="">{t("Not bound")}</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.displayName}
                {u.email ? ` · ${u.email}` : ""}
              </option>
            ))}
          </Select>
          <Err text={show("principalId")} />
        </Field>
        <Field label="Cost center">
          <Input value={f.costCentre} onChange={set("costCentre")} placeholder={t("e.g. CC-AFM")} />
        </Field>
        <Field label="Notes">
          <Input value={f.notes} onChange={set("notes")} />
        </Field>
      </Section>

      {preview && canPay && (
        <div className={s.coverage} data-testid="hr-f-preview">
          {preview.costed ? (
            <div>
              {t("This person costs the airport {month} a month.", { month: sar(preview.monthlyCost) })}{" "}
              {showHourly && t("That is {hour} for each hour worked.", { hour: rate(preview.hourlyRate) })}{" "}
              {!outsourced &&
                t(
                  "GOSI {pct}% ({regime}), end of service {eosb} a month; overtime is paid at {ot} an hour (art. 107).",
                  {
                    pct: preview.gosiEmployerPct ?? 0,
                    regime: t(preview.gosiRegime ?? "—"),
                    eosb: sar(preview.eosbAccrual),
                    ot: rate(preview.overtimeRate),
                  },
                )}
            </div>
          ) : (
            <div>{t("No pay yet. This person is counted but not costed.")}</div>
          )}
        </div>
      )}

      {failed && <p className={s.err}>{failed instanceof Error ? failed.message : String(failed)}</p>}
      <div className={s.formFoot}>
        <Button type="submit" variant="primary" disabled={pending} data-testid="hr-f-save">
          {pending ? t("Saving…") : adding ? t("Add the employee") : t("Save the changes")}
        </Button>
        <Button onClick={onClose}>{t("Cancel")}</Button>
      </div>
    </form>
  );
}
